import { SearchIcon, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCommandPalette } from './CommandPaletteProvider';
import { CommandPaletteResults } from './CommandPaletteResults';
import { useCommandPaletteState } from './hooks/useCommandPaletteState';
import { useRecentItems } from './hooks/useRecentItems';
import type { CommandResult } from './types';

export function CommandPalette() {
  const { open, setOpen, providers } = useCommandPalette();
  const { input, mode, activeScope, setInput, setRawInput, clearScope, reset } =
    useCommandPaletteState(providers);
  const { addRecent } = useRecentItems();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const allItemIdsRef = useRef<string[]>([]);
  const prevIdsKeyRef = useRef('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Poll DOM for item IDs while dialog is open — catches async child updates
  useEffect(() => {
    if (!open) return;

    let rafId: number;
    const sync = () => {
      if (containerRef.current) {
        const items = containerRef.current.querySelectorAll('[data-item-id]');
        const ids = Array.from(items)
          .map((el) => el.getAttribute('data-item-id'))
          .filter((id): id is string => id !== null);
        const key = ids.join(',');
        if (key !== prevIdsKeyRef.current) {
          prevIdsKeyRef.current = key;
          allItemIdsRef.current = ids;
          setSelectedId((current) => {
            if (ids.length === 0) return null;
            if (current && ids.includes(current)) return current;
            return ids[0];
          });
        }
      }
      rafId = requestAnimationFrame(sync);
    };
    rafId = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(rafId);
  }, [open]);

  // Scroll selected item into view
  useEffect(() => {
    if (!selectedId || !containerRef.current) return;
    const el = containerRef.current.querySelector(`[data-item-id="${CSS.escape(selectedId)}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedId]);

  // Resolve "View all" href for the active scoped provider
  const scopedViewAllHref = useMemo(() => {
    if (!activeScope) return null;
    const provider = providers.find((p) => p.id === activeScope.providerId);
    if (!provider?.viewAllHref) return null;
    return provider.viewAllHref(input);
  }, [activeScope, providers, input]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (!nextOpen) {
        reset();
        setSelectedId(null);
      }
    },
    [setOpen, reset]
  );

  const handleSelectResult = useCallback(
    (result: CommandResult) => {
      result.onSelect();
      if (result.id.startsWith('page:')) {
        addRecent({
          id: result.id,
          label: result.label,
          subtitle: result.subtitle,
          href: result.id.replace('page:', ''),
        });
      }
      handleOpenChange(false);
    },
    [handleOpenChange, addRecent]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Backspace on empty input clears scope badge
      if (e.key === 'Backspace' && !input && activeScope) {
        e.preventDefault();
        clearScope();
        return;
      }

      // Cmd+Enter → view all / search all
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && scopedViewAllHref) {
        e.preventDefault();
        navigate(scopedViewAllHref);
        handleOpenChange(false);
        return;
      }

      // Arrow navigation
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedId((current) => {
          const ids = allItemIdsRef.current;
          const idx = current ? ids.indexOf(current) : -1;
          return ids[Math.min(idx + 1, ids.length - 1)] ?? current;
        });
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedId((current) => {
          const ids = allItemIdsRef.current;
          const idx = current ? ids.indexOf(current) : 0;
          return ids[Math.max(idx - 1, 0)] ?? current;
        });
        return;
      }

      // Enter to select — click the selected DOM element
      if (e.key === 'Enter' && !(e.metaKey || e.ctrlKey) && selectedId && containerRef.current) {
        e.preventDefault();
        const el = containerRef.current.querySelector(`[data-item-id="${CSS.escape(selectedId)}"]`);
        if (el instanceof HTMLElement) el.click();
      }
    },
    [input, activeScope, clearScope, scopedViewAllHref, navigate, handleOpenChange, selectedId]
  );

  // Focus input when dialog opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="overflow-hidden p-0 top-[20%] translate-y-0"
        showCloseButton={false}
        onKeyDown={handleKeyDown}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Command Palette</DialogTitle>
          <DialogDescription>Search and navigate</DialogDescription>
        </DialogHeader>
        {/* Input with optional scope badge */}
        <div className="flex items-center gap-2 border-b px-3 h-12">
          <SearchIcon className="h-4 w-4 shrink-0 opacity-50" />
          {activeScope && (
            <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary shrink-0">
              {activeScope.label}
              <button
                type="button"
                onClick={() => {
                  clearScope();
                  inputRef.current?.focus();
                }}
                className="ml-0.5 rounded-sm hover:bg-primary/20 transition-colors"
                aria-label={`Remove ${activeScope.label} filter`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={
              activeScope
                ? `Search ${activeScope.label.toLowerCase()}...`
                : mode.type === 'action'
                  ? 'Run an action...'
                  : `Search or filter with ${providers
                      .filter((p) => p.scopeKeyword)
                      .map((p) => `#${p.scopeKeyword}`)
                      .join(' ')} ...`
            }
            className="placeholder:text-muted-foreground flex h-10 w-full bg-transparent text-sm outline-hidden"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {activeScope && scopedViewAllHref && (
            <button
              type="button"
              onClick={() => {
                navigate(scopedViewAllHref);
                handleOpenChange(false);
              }}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              title={`${input ? `Search all ${activeScope.label.toLowerCase()} for "${input}"` : `View all ${activeScope.label.toLowerCase()}`} (⌘↵)`}
            >
              {input ? 'Search all' : 'View all'}
              <kbd className="rounded border bg-muted px-1 font-mono text-[10px] text-muted-foreground/60">
                ⌘↵
              </kbd>
            </button>
          )}
        </div>

        <div ref={containerRef}>
          <CommandPaletteResults
            mode={mode}
            providers={providers}
            selectedId={selectedId}
            onSelect={handleSelectResult}
            onHover={setSelectedId}
            onSetRawInput={setRawInput}
            onClose={() => handleOpenChange(false)}
          />
        </div>

        {/* Keyboard hints footer */}
        <div className="flex items-center gap-3 border-t px-3 py-1.5 text-[11px] text-muted-foreground/50">
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↑↓</kbd> navigate
          </span>
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">↵</kbd> select
          </span>
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">#</kbd> filter
          </span>
          <span>
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">&gt;</kbd> actions
          </span>
          <span className="ml-auto">
            <kbd className="rounded border bg-muted px-1 font-mono text-[10px]">esc</kbd> close
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
